import autoTable from "jspdf-autotable";
import { convertAmountToWords } from "./NumberWordHelper";

const InvoiceDoLayoutPrintHeader = (
    doc,
    pageWidth,
    pageHeight,
    invoiceData,
    REPORT_FOOTER_IMAGE,
    REPORT_HEADER_IMAGE,
    COMPANY_NAME,
    VAT_PERCENTAGE,
    qrCodeImage,
    reportSealImage,
) => {
    const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
        ? JSON.parse(localStorage.getItem("currentUser"))
        : "";
    const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

    if (!invoiceData || invoiceData.length === 0) return;

    doc.setFont("helvetica", "normal");

    const firstItem = invoiceData[0];
    const isVatApplied = firstItem.deliv_site === "VAT Applied";
    const vatPercent = isVatApplied ? Number(VATPERCENTAGE || 0) : 0;
    const vatRate = vatPercent / 100;

    const headerMargin = 12;
    const headerHeight = 30;
    const margin = 10;

    // === Dynamic Footer Height Calculation ===
    let footerHeight = 30;
    if (REPORT_FOOTER_IMAGE) {
        try {
            const imgProps = doc.getImageProperties(REPORT_FOOTER_IMAGE);
            const footerWidth = pageWidth - 2 * headerMargin;
            footerHeight = (footerWidth / imgProps.width) * imgProps.height;
        } catch (e) {
            console.warn("Could not calculate footer image properties", e);
        }
    }

    const sectionHeight = 25;
    const signatureHeight = 30;
    const bottomBlockHeight = sectionHeight + signatureHeight + 25;

    const companyTRN =
        CURRENT_USER_OBJECT[0]?.trn || CURRENT_USER_OBJECT[0]?.TRN || "-";

    // ==========================================
    // REUSABLE STATIONERY HEADER RENDERER
    // ==========================================
    const drawHeaderSection = (pdfDoc) => {
        let currentY = 0;

        // === Header Image Banner ===
        if (REPORT_HEADER_IMAGE) {
            pdfDoc.addImage(
                REPORT_HEADER_IMAGE,
                "PNG",
                headerMargin,
                currentY,
                pageWidth - 2 * headerMargin,
                headerHeight,
            );
        }

        currentY += headerHeight + 5;

        // === Document Title Header ===
        pdfDoc.setFontSize(16);
        pdfDoc.setFont(undefined, "bold");
        pdfDoc.text(
            "DELIVERY ORDER",
            pageWidth / 2,
            currentY,
            { align: "center" },
        );
        currentY += 6;

        pdfDoc.setFontSize(10);
        pdfDoc.setFont(undefined, "normal");
        pdfDoc.text(`TRN NO : ${companyTRN}`, pageWidth / 2, currentY, {
            align: "center",
        });
        currentY += 5;

        pdfDoc.setFontSize(10);

        const boxGap = 2;
        const contentWidth = pageWidth - 2 * margin;
        const leftBoxWidth = contentWidth * 0.65;

        // Build address metadata
        const rawAddress = [
            firstItem.address1,
            firstItem.address2,
            firstItem.address3,
        ]
            .filter((addr) => addr && addr.trim() !== "")
            .join(", ");
        const wrappedAddressLines = pdfDoc.splitTextToSize(rawAddress, leftBoxWidth - 4);

        const leftTextLines = [
            firstItem.custref || firstItem.tr_desc || "Client Name",
            ...wrappedAddressLines,
            `TEL: ${firstItem.tel || "-"}`,
            `TRN: ${firstItem.governor || "-"}`,
        ];

        const lineHeightLeft = 5.5;
        const leftBoxHeight = leftTextLines.length * lineHeightLeft + 2;

        const rightTextLines = [
            `Invoice No: ${firstItem.inv_no || "-"}`,
            `Date: ${firstItem.inv_date ? new Date(firstItem.inv_date).toLocaleDateString("en-GB") : "-"}`,
            `LPO No: ${firstItem.lpo_no || "-"}`,
            `DO No: ${firstItem.do_data || "-"}`,
            `Salesman: ${firstItem.sale_man || "-"}`,
        ];
        const lineHeightRight = 5.5;
        const rightBoxHeight = rightTextLines.length * lineHeightRight + 2;
        const boxHeight = Math.max(leftBoxHeight, rightBoxHeight);

        // Draw left layout metadata
        let leftY = currentY + 4;
        for (let i = 0; i < leftTextLines.length; i++) {
            pdfDoc.setFont(undefined, i === 0 ? "bold" : "normal");
            pdfDoc.text(leftTextLines[i], margin + 2, leftY);
            leftY += lineHeightLeft;
        }

        // Draw right layout metadata
        const rightBoxX = margin + leftBoxWidth + boxGap;
        let rightY = currentY + 4;
        pdfDoc.setFont(undefined, "normal");
        for (let i = 0; i < rightTextLines.length; i++) {
            pdfDoc.text(rightTextLines[i], rightBoxX + 2, rightY);
            rightY += lineHeightRight;
        }

        return currentY + boxHeight + 5;
    };

    // Run header layout on initial page execution
    const startTableY = drawHeaderSection(doc);

    // === Items Table Configuration (Reduced Columns) ===
    const tableHead = [["Code", "Description", "Unit", "Qty"]];

    const tableBody = invoiceData.map((item) => {
        const qty = item.tr_qty || 0;
        return [
            item.code || item.tr_code || "",
            item.idesc || item.tr_desc || "",
            item.unit || item.uom || "",
            qty.toString()
        ];
    });

    const columnStyles = {
        0: { halign: "left", cellWidth: 30 },
        1: { halign: "left", cellWidth: 110 },
        2: { halign: "center", cellWidth: 25 },
        3: { halign: "center", cellWidth: 25 },
    };

    autoTable(doc, {
        head: tableHead,
        body: tableBody,
        startY: startTableY,
        theme: "plain",
        tableWidth: "auto",
        margin: {
            left: margin,
            right: margin,
            bottom: footerHeight + 5,
        },
        styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
        headStyles: {
            fontStyle: "bold",
            halign: "center",
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            cellPadding: 1, // Reduced padding applied here
        },
        columnStyles,
        didDrawPage: function (data) {
            if (data.pageNumber > 1) {
                drawHeaderSection(doc);
            }
        },
        didDrawCell: function (data) {
            const cell = data.cell;
            doc.setDrawColor(0);
            doc.setLineWidth(0.1);

            doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
            doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);

            if (data.row.section === "head") {
                doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
                doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
            } else if (data.row.index === data.table.body.length - 1) {
                doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
            }
        },
    });

    // ─── After table: check if bottom block fits on current page ──────────────
    const tableEndY = doc.lastAutoTable.finalY || startTableY;
    const contentWidth = pageWidth - 2 * margin;
    const spaceLeft = pageHeight - footerHeight - 5 - tableEndY;

    if (spaceLeft < bottomBlockHeight) {
        doc.addPage();
        drawHeaderSection(doc);
    }

    // === Minimal Base Calculations ===
    const displaySectionHeight = 15;
    const sectionTopY = pageHeight - footerHeight - signatureHeight - displaySectionHeight - 5;

    // === Signature Section ===
    const sigY = sectionTopY + displaySectionHeight + signatureHeight - 10;

    doc.line(15, sigY, 80, sigY);
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.text("Receiver's Signature", 45, sigY + 5, { align: "center" });

    if (reportSealImage) {
        const sealSize = 20;
        const rightSignatureCenterX = pageWidth - 45;
        const sealX = rightSignatureCenterX - (sealSize / 2);
        const sealY = sigY - sealSize;
        doc.addImage(reportSealImage, "PNG", sealX, sealY, sealSize, sealSize);
    }

    doc.line(pageWidth - 80, sigY, pageWidth - 15, sigY);
    doc.text(`For ${COMPANY_NAME}`, pageWidth - 45, sigY + 5, { align: "center" });

    // === Stamp "Page X of Y" and Footer Images on all pages ===
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);

        if (REPORT_FOOTER_IMAGE) {
            doc.addImage(
                REPORT_FOOTER_IMAGE,
                "PNG",
                headerMargin,
                pageHeight - footerHeight - 5,
                pageWidth - 2 * headerMargin,
                footerHeight,
            );
        }

        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
            `Page ${p} of ${totalPages}`,
            pageWidth / 2,
            pageHeight - footerHeight - 8,
            { align: "center" },
        );
        doc.setTextColor(0);
    }
};

export default InvoiceDoLayoutPrintHeader;